import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Check, Triangle, ArrowRight, RotateCcw, ShieldCheck, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/tracker";
import { LeadForm } from "@/components/LeadForm";
import { useSeo } from "@/lib/seo";

export default function Resultat() {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [ring, setRing] = useState(0);
  const payload = location.state?.payload;
  useSeo("Votre carte de protection — Mon Projet Protégé");

  useEffect(() => {
    if (!payload) { navigate("/diagnostic"); return; }
    api.evaluate(payload).then((r) => {
      setResult(r);
      track("diagnostic_complete", { label: r.family || "", meta: { score: r.score, family: r.family, gaps: r.gaps, strengths: r.strengths, qui: r.qui } });
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!result) return;
    let raf; const start = performance.now();
    const animate = (t) => {
      const p = Math.min((t - start) / 900, 1);
      setRing(Math.round(result.score * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [result]);

  const share = async () => {
    const text = result ? `Ma carte de protection financière : ${result.score}% couvert.` : "";
    try {
      if (navigator.share) await navigator.share({ title: "Mon Projet Protégé", text, url: window.location.origin });
      else { await navigator.clipboard.writeText(`${text} ${window.location.origin}`); toast.success("Copié dans le presse-papiers."); }
    } catch { /* cancelled */ }
  };

  if (!result) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Analyse de votre situation…</div>;

  const R = 84, C = 2 * Math.PI * 84;
  const color = result.score >= 70 ? "#1F7A5C" : result.score >= 45 ? "#C89A3E" : "#B23A48";
  const verdict = result.score >= 70 ? "Belle protection indicative — quelques optimisations possibles."
    : result.score >= 45 ? "Bon départ — plusieurs leviers à activer." : "Des priorités claires à mettre en place.";
  const dateStr = new Date().toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="protection-card-container" className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
      {/* ---------- Certificat ---------- */}
      <div className="certificate relative rounded-[28px] bg-white p-1.5 animate-rise" style={{ boxShadow: "0 30px 70px -30px rgba(1,33,65,0.45)" }}>
        <div className="relative rounded-[22px] border-2 border-[#012141]/15 px-6 sm:px-12 py-10 sm:py-12 overflow-hidden">
          {/* ornamental corners */}
          <span className="pointer-events-none absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#C89A3E]/60 rounded-tl-lg" />
          <span className="pointer-events-none absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-[#C89A3E]/60 rounded-tr-lg" />
          <span className="pointer-events-none absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-[#C89A3E]/60 rounded-bl-lg" />
          <span className="pointer-events-none absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#C89A3E]/60 rounded-br-lg" />

          {/* header */}
          <div className="flex flex-col items-center text-center">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#012141] text-[#C89A3E] shadow-lg"><ShieldCheck size={24} /></span>
            <div className="mt-3 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#C89A3E]">Certificat indicatif</div>
            <h1 className="mt-1.5 text-2xl sm:text-3xl font-bold text-[#012141] font-heading">Carte de protection financière</h1>
            <div className="mt-2 h-px w-24 gold-rule" />
          </div>

          {/* score ring */}
          <div className="mt-8 flex flex-col items-center">
            <div className="relative w-52 h-52">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r={R} fill="none" stroke="#01214114" strokeWidth="15" />
                <circle cx="100" cy="100" r={R} fill="none" stroke={color} strokeWidth="15" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C - (C * ring) / 100} style={{ transition: "stroke 0.4s" }} />
              </svg>
              <div className="absolute inset-0 grid place-content-center text-center">
                <div data-testid="protection-score-badge" className="text-5xl font-bold text-[#012141] font-heading">{ring}%</div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">indicatif</div>
              </div>
            </div>
            <p className="mt-4 text-slate-600 font-medium text-center max-w-md">{verdict}</p>
            <p className="mt-2 text-xs text-slate-400 text-center max-w-md">Résultat indicatif — il ne remplace pas une analyse complète avec un conseiller.</p>
          </div>

          {/* breakdown */}
          <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-[#1F7A5C]/20 bg-[#1F7A5C]/5 p-5">
              <div className="flex items-center gap-2.5 mb-3"><span className="grid place-items-center w-9 h-9 rounded-xl text-white" style={{ backgroundColor: "#1F7A5C" }}><Check size={18} /></span><h2 className="text-base font-bold text-[#012141] font-heading">Vos forces</h2></div>
              <div className="space-y-2.5">
                {result.strengths.length === 0 && <p className="text-sm text-slate-400">Aucune force détectée pour l'instant.</p>}
                {result.strengths.map((s, i) => (
                  <div key={s.code} data-testid={`strength-item-${i}`} className="flex items-start gap-2.5">
                    <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: "#1F7A5C" }} />
                    <div><div className="text-sm font-bold text-[#012141]">{s.code}</div><div className="text-sm text-slate-600 leading-relaxed">{s.message}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-[#C89A3E]/25 bg-[#C89A3E]/8 p-5">
              <div className="flex items-center gap-2.5 mb-3"><span className="grid place-items-center w-9 h-9 rounded-xl text-white" style={{ backgroundColor: "#C89A3E" }}><Triangle size={16} /></span><h2 className="text-base font-bold text-[#012141] font-heading">Points à renforcer</h2></div>
              <div className="space-y-2.5">
                {result.gaps.length === 0 && <p className="text-sm text-slate-400">Rien à signaler — bravo !</p>}
                {result.gaps.map((g, i) => (
                  <div key={g.code} data-testid={`gap-item-${i}`} className="flex items-start gap-2.5">
                    <Triangle size={14} className="mt-1 flex-shrink-0" style={{ color: "#C89A3E" }} />
                    <div><div className="text-sm font-bold text-[#012141]">{g.code}</div><div className="text-sm text-slate-600 leading-relaxed">{g.message}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* certificate footer + seal */}
          <div className="mt-9 pt-5 border-t border-[#012141]/10 flex items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Profil</div>
              <div className="text-sm font-semibold text-[#012141] capitalize">{result.family || "Particulier"}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-2">Émis le</div>
              <div className="text-sm font-semibold text-[#012141]">{dateStr}</div>
            </div>
            <div className="relative grid place-items-center w-24 h-24 rounded-full border-2 border-[#C89A3E]/50 text-[#012141] shrink-0">
              <div className="absolute inset-1.5 rounded-full border border-dashed border-[#012141]/25" />
              <ShieldCheck size={26} className="text-[#C89A3E]" />
              <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.15em] text-[#012141] text-center leading-tight">Mon Projet<br />Protégé · QC</span>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Actions (non imprimées) ---------- */}
      <div className="no-print mt-6 flex flex-wrap items-center justify-center gap-3">
        <button data-testid="result-print-btn" onClick={() => window.print()}
          className="btn-gold inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C89A3E] text-[#012141] font-bold hover:brightness-105 transition-[filter]"><Printer size={17} /> Imprimer / PDF</button>
        <button data-testid="result-share-btn" onClick={share}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#012141]/20 text-[#012141] font-semibold hover:border-[#C89A3E] transition-colors"><Share2 size={16} /> Partager</button>
      </div>

      <div className="no-print mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="rounded-3xl px-6 sm:px-8 py-8 relative overflow-hidden" style={{ background: "#012141" }}>
          <div className="absolute -bottom-12 -left-8 w-56 h-56 rounded-full blob" style={{ background: "#C89A3E" }} />
          <div className="relative">
            <h2 className="text-2xl font-bold text-white font-heading">Passez à l'action</h2>
            <p className="mt-2 text-white/70">Transformez vos points à renforcer en plan concret avec un conseiller.</p>
            <Link to="/formations" data-testid="result-formations-btn" className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#012141] font-semibold hover:bg-white/90 transition-all">Voir les formations <ArrowRight size={18} /></Link>
          </div>
        </div>
        <LeadForm profil={result.family} score={result.score} />
      </div>

      <div className="no-print mt-6 text-center space-y-2">
        <button data-testid="result-restart-btn" onClick={() => navigate("/diagnostic")} className="inline-flex items-center gap-2 text-slate-500 font-semibold hover:text-[#012141] transition-colors"><RotateCcw size={16} /> Refaire le diagnostic</button>
        <div>
          <Link to="/mon-parcours" data-testid="result-parcours-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 font-medium hover:text-[#C89A3E] transition-colors">En savoir plus sur votre conseiller <ArrowRight size={14} /></Link>
        </div>
      </div>
    </div>
  );
}
